import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Chip, CircularProgress, Divider, Input, Sheet, Typography } from '@mui/joy';
import { directSupabase } from '../lib/supabaseClient';

const PAGE_SIZE = 50;

type ChangeLogRow = {
  id: string;
  title: string;
  details: string | null;
  created_at: string;
};

const DEFAULT_CHANGE_LOG_ITEMS: string[] = [
  'Added L-shape handedness control so door/window sides can be flipped.',
  'Fixed WG width field reverting after Enter during bay-related edits.',
  'Aligned downstream QW and VD rendering with WG L-shape geometry.',
  'Refined L-shape presentation sash sizing to remove over-wide flag visuals.',
  'Corrected internal presentation sash offset where window adjoins the door.',
  'Resolved top sash height mismatch between internal and external presentation.',
  'Fixed compile issue in renderer by replacing undefined isL reference.',
  'Introduced union cill mode controls: Door Only, Window Only, Door and Window.',
  'Scoped union cill segments so cill placement reflects selected union mode.',
  'Retained backward compatibility with legacy cill include-upper payloads.',
  'Updated payload hydration paths to normalize and persist union cill mode.',
  'Refined cill rendering so internal seam edges do not get external overhang treatment.',
  'Reworked sash join inset logic for L presets to match seam behavior.',
  'Moved cill render order to draw above frame elements in presentation.',
  'Moved cill render order to draw above frame elements in design mode.',
  'Removed asymmetric cill overhang and made cills flush with frame edges.',
  'Simplified cill depth cues by removing drip and heavy pseudo-3D effects.',
  'Added explicit cill end caps to reinforce symmetric flush rendering.',
  'Reverted directional shadow experiment after visual regression feedback.',
  'Extended perimeter shadow height so cill depth is included where relevant.',
  'Implemented segmented union shadow extension to avoid overextending unrelated levels.',
  'Applied shadow-extension logic consistently across main and side previews.',
  'Raised max fenestration height constraint from 2400mm to 8000mm.',
  'Added L-shape Cut Y dimension line in WG design overlay.',
  'Made Cut Y label clickable with inline editing and keyboard commit/cancel.',
  'Committed pending Cut Y edits on blur and before Use Design export.',
  'Scaled Cut Y display with visual-dimension methodology used by pane labels.',
  'Added minimum interactive dimension threshold to hide tiny non-editable labels.',
  'Suppressed inside-view design dimensions in downstream modal render path.',
  'Adjusted outer-right dimension lanes so Cut Y and overall height do not collide.',
  'Added extra WG design-canvas padding to prevent dimension clipping.',
  'Added vertical bias in VD cards so bottom dimensions are not truncated.',
  'Prioritized WG payload dimensions over bay tangent metadata in VD hydration.',
  'Stopped stale save paths from overwriting WG-authored door and window widths.',
  'Reordered payload precedence to favor latest v2-lite payload snapshot on save.',
  'Hydrated edit-mode dimensions from template payload when available.',
  'Persisted cill union mode metadata into specs options during save.',
  'Appended cill placement labels to summary rows across edit and read-only views.',
  'Propagated cill placement and payload extras into window order conversion.',
  'Expanded quote read-only table spacing to stop label and value overlap.',
  'Expanded document read-only table spacing for consistent legibility.',
  'Added Door and Window as a new quote wizard modal type option.',
  'Detected mixed handle sets to infer door-window type automatically.',
  'Mapped door-window type to existing door style pools and template filters.',
  'Redesigned supplier technical PDF to prioritize a larger design-outside image.',
  'Removed presentation views from technical page and moved them to appendix pages.',
  'Added always-on presentation appendix pages with reference and both presentation views.',
  'Improved supplier dimension label contrast using boxed high-contrast styling.',
  'Fixed A1 A2 glass tag scaling so label boxes track text size reliably.',
  'Added and then kept only Open Direction in supplier specs while removing duplicate Opens row.'
];

const createSeedRows = () => {
  const base = Date.now();
  return DEFAULT_CHANGE_LOG_ITEMS.map((summary, idx) => {
    const offsetMinutes = DEFAULT_CHANGE_LOG_ITEMS.length - idx;
    return {
      title: summary,
      details: null,
      created_at: new Date(base - offsetMinutes * 60 * 1000).toISOString(),
      source: 'seed',
    };
  });
};

const ChangeLogPanel: React.FC = () => {
  const [rows, setRows] = useState<ChangeLogRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>('');
  const [newEntry, setNewEntry] = useState('');
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(totalCount / PAGE_SIZE)), [totalCount]);

  const fetchPage = useCallback(async (targetPage: number) => {
    setLoading(true);
    setError('');
    const from = (targetPage - 1) * PAGE_SIZE;
    const to = from + PAGE_SIZE - 1;

    const query = (directSupabase as any)
      .from('change_log_entries')
      .select('id,title,details,created_at', { count: 'exact' })
      .order('created_at', { ascending: false })
      .range(from, to);

    const { data, error: queryError, count } = await query;
    if (queryError) {
      setRows([]);
      setTotalCount(0);
      setError(queryError.message);
      setLoading(false);
      return;
    }

    setRows((data || []) as ChangeLogRow[]);
    setTotalCount(Number(count || 0));
    setLoading(false);
  }, []);

  const ensureSeed = useCallback(async () => {
    const countQuery = (directSupabase as any)
      .from('change_log_entries')
      .select('id', { count: 'exact', head: true });
    const { count, error: countError } = await countQuery;
    if (countError) return;
    if (Number(count || 0) > 0) return;
    await (directSupabase as any).from('change_log_entries').insert(createSeedRows());
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      await ensureSeed();
      if (mounted) await fetchPage(page);
    })();
    return () => {
      mounted = false;
    };
  }, [ensureSeed, fetchPage, page]);

  const handleAddEntry = async () => {
    const title = newEntry.trim();
    if (!title) return;
    setSaving(true);
    setError('');
    const { error: insertError } = await (directSupabase as any)
      .from('change_log_entries')
      .insert({
        title,
        details: null,
        source: 'manual',
      });
    setSaving(false);
    if (insertError) {
      setError(insertError.message);
      return;
    }
    setNewEntry('');
    setPage(1);
    await fetchPage(1);
  };

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString();
  };

  return (
    <Box>
      <Typography level="title-lg" sx={{ mb: 1 }}>Change Log</Typography>
      <Typography level="body-sm" sx={{ color: 'neutral.600', mb: 2 }}>
        Track product updates with automatic timestamps and 50-item pagination.
      </Typography>

      <Sheet variant="soft" sx={{ p: 2, borderRadius: 'sm', mb: 2 }}>
        <Typography level="body-sm" sx={{ mb: 1, fontWeight: 700 }}>Add Entry</Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Input
            placeholder="Describe the change (one sentence)"
            value={newEntry}
            onChange={(e) => setNewEntry(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddEntry();
              }
            }}
            sx={{ minWidth: 360, flex: 1 }}
          />
          <Button onClick={handleAddEntry} loading={saving}>Add</Button>
        </Box>
      </Sheet>

      {error ? (
        <Sheet variant="outlined" color="danger" sx={{ p: 1.5, mb: 2, borderRadius: 'sm' }}>
          <Typography level="body-sm" color="danger">
            {error}
          </Typography>
        </Sheet>
      ) : null}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Chip size="sm" variant="soft">{totalCount} total entries</Chip>
        <Typography level="body-xs" sx={{ color: 'neutral.600' }}>
          Page {page} of {totalPages}
        </Typography>
      </Box>

      <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size="sm" />
          </Box>
        ) : rows.length === 0 ? (
          <Box sx={{ p: 2 }}>
            <Typography level="body-sm" sx={{ color: 'neutral.600' }}>No change log entries yet.</Typography>
          </Box>
        ) : (
          rows.map((row, idx) => (
            <Box key={row.id}>
              <Box sx={{ p: 1.5 }}>
                <Typography level="body-sm" sx={{ fontWeight: 700 }}>{row.title}</Typography>
                {row.details ? (
                  <Typography level="body-sm" sx={{ color: 'neutral.700', mt: 0.5 }}>{row.details}</Typography>
                ) : null}
                <Typography level="body-xs" sx={{ color: 'neutral.500', mt: 0.75 }}>
                  {formatDate(row.created_at)}
                </Typography>
              </Box>
              {idx < rows.length - 1 ? <Divider /> : null}
            </Box>
          ))
        )}
      </Sheet>

      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 2 }}>
        <Button
          variant="outlined"
          disabled={page <= 1 || loading}
          onClick={() => setPage((p) => Math.max(1, p - 1))}
        >
          Previous
        </Button>
        <Button
          variant="outlined"
          disabled={page >= totalPages || loading}
          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
        >
          Next
        </Button>
      </Box>
    </Box>
  );
};

export default ChangeLogPanel;
